import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    Button,
    Space,
    Form,
    Input,
    DatePicker,
    Switch,
    InputNumber,
    message,
    Alert,
    Divider,
    Timeline,
    Steps,
    List,
    Collapse,
    Radio,
    Table,
    Tag,
    Row,
    Col,
    Modal,
    Progress,
    Select,
    Popconfirm,
    Spin,
    Tooltip
} from 'antd';
import {
    ArrowLeftOutlined,
    LineChartOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    RobotOutlined,
    LoadingOutlined,
    StopOutlined,
    SendOutlined,
    BellOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { TrainingType } from '../types/training';
import dayjs from 'dayjs';
import {
    adminGetTrainingById,
    adminUpdateTraining,
    adminGenerateAIQuestions,
    adminToggleTrainingStatus,
    adminClearSubmissionsByTraining,
    adminDeleteTraining,
    adminSummarizeChanges,
    adminSendProgramUpdateNotification
} from '../services/adminService';
import { publishMoodleProgram, createMoodleQuiz, getMoodleQuizQuestions, updateMoodleQuizQuestions, deleteMoodleQuiz } from '../services/moodleService';
import { parseModuleId } from '../utils/moduleUtils';
import { detectCommonChanges } from '../utils/changeDetection';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface SubjectiveQuestion {
    id: string;
    moodleId?: number; // set when loaded from Moodle, 0 for new questions
    question: string;
    answer: string;
    codeSnippet: string;
}

interface ModuleEntry {
    id: string;
    name: string;
    description: string;
    difficulty: string;
    questionCount: number;
    estimatedTime: number;
    attemptLimit: number; // 0 = unlimited
    questions: SubjectiveQuestion[];
    generated: boolean;
    generating: boolean;
    isExisting?: boolean;
    questionsLoaded?: boolean; // existing module questions fetched from Moodle
    moodleInstance?: number; // resolved quiz DB ID, set after first fetch
    originalName?: string;  // name at load time, used to detect renames
    extraPrompt?: string;
}

const EditSubjectiveProgram: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [initialValues, setInitialValues] = useState<any>(null);
    const [modules, setModules] = useState<ModuleEntry[]>([]);
    // Existing modules removed in this edit - their Moodle quizzes are deleted on save.
    const [pendingDeletions, setPendingDeletions] = useState<{ name: string; quizId: number }[]>([]);
    const [isActive, setIsActive] = useState(true);
    const [isPublished, setIsPublished] = useState(true);
    const [notifyParticipant, setNotifyParticipant] = useState(true);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

    // Progress Modal State
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');

    // Questions Review Modal State
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [activeReviewModuleId, setActiveReviewModuleId] = useState<string | null>(null);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    const [form] = Form.useForm();
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            fetchTrainingData(parseInt(id));
        }
    }, [id]);

    const fetchTrainingData = async (trainingId: number) => {
        try {
            setFetching(true);
            const data = await adminGetTrainingById(trainingId);
            setInitialValues(data);
            const isEvergreen = !data.startDate || !data.endDate;
            form.setFieldsValue({
                trainingId: data.trainingId,
                trainingName: data.trainingName,
                dateRange: isEvergreen ? null : [
                    data.startDate ? dayjs(data.startDate.split('T')[0]) : null,
                    data.endDate ? dayjs(data.endDate.split('T')[0]) : null
                ],
                isEvergreen: isEvergreen,
                passingPercentage: data.passingPercentage,
                estimatedEffortHours: data.estimatedEffortHours,
                trainingLink: data.trainingLink,
                audienceEmails: data.audienceEmails,
                stakeholderEmails: data.stakeholderEmails,
                notificationVersion: (data.notificationVersion === null || data.notificationVersion === undefined || data.notificationVersion === '') ? '1.0' : String(data.notificationVersion),
                trainingType: data.trainingType || TrainingType.OPEN
            });
            const isDraft = data.ispublished === false;
            setIsPublished(!isDraft);
            setIsActive(!!data.active || isDraft);

            const existingModules = data.assessments ? data.assessments.split('|').map((s: string) => s.trim()).filter(Boolean) : [];
            const mappedModules: ModuleEntry[] = existingModules.map(m => {
                const { name, description, estimatedTime, attemptLimit } = parseModuleId(m);

                return {
                    id: Math.random().toString(36).substr(2, 9),
                    name: name,
                    originalName: name,
                    description: description,
                    difficulty: 'N/A',
                    questionCount: 0,
                    estimatedTime: estimatedTime,
                    attemptLimit: attemptLimit,
                    questions: [],
                    generated: true,
                    generating: false,
                    isExisting: true
                };
            });
            setModules(mappedModules);

        } catch (error) {
            message.error('Failed to load training data');
            navigate('/admin');
        } finally {
            setFetching(false);
        }
    };

    const addModuleRow = () => {
        const newId = Math.random().toString(36).substr(2, 9);
        setModules([...modules, {
            id: newId,
            name: '',
            description: '',
            difficulty: 'Medium',
            questionCount: 5,
            estimatedTime: 30,
            attemptLimit: 0,
            questions: [],
            generated: false,
            generating: false,
            isExisting: false,
            extraPrompt: ''
        }]);
    };

    const removeModuleRow = (mod: ModuleEntry) => {
        if (modules.length <= 1) {
            message.error('At least one module must remain in the program.');
            return;
        }

        if (mod.isExisting) {
            setPendingDeletions([...pendingDeletions, { name: mod.originalName || mod.name, quizId: mod.moodleInstance || 0 }]);
        }
        setModules(modules.filter(m => m.id !== mod.id));
    };

    const updateModuleField = (id: string, field: keyof ModuleEntry, value: any) => {
        setModules(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const handleGenerateForModule = async (module: ModuleEntry) => {
        const currentFormValues = form.getFieldsValue();
        const trainingName = currentFormValues.trainingName || "General Training";

        if (!module.name || !module.description) {
            message.warning("Please provide a Module Name and Description/Topic Focus before generating.");
            return;
        }

        setModules(prev => prev.map(m =>
            m.id === module.id ? { ...m, generating: true, generated: false, questions: [] } : m
        ));

        try {
            // Fetch questions and estimated time in one batch
            const aiData = await adminGenerateAIQuestions(
                `Training Program: ${trainingName}. Module Name: ${module.name}. Module Focus: ${module.description}. Generate ${module.questionCount} distinct questions.`,
                module.difficulty,
                module.questionCount,
                module.extraPrompt
            );

            // Format with internal IDs for React table
            const formattedQs = aiData.questions.slice(0, module.questionCount).map((q: any) => ({
                id: Math.random().toString(36).substr(2, 9),
                question: q.question || q.Question || '',
                answer: q.answer || q.Answer || '',
                codeSnippet: q.codeSnippet || q.CodeSnippet || ''
            }));

            setModules(prev => prev.map(m =>
                m.id === module.id ? {
                    ...m,
                    questions: formattedQs,
                    estimatedTime: aiData.estimatedTimeMinutes || m.estimatedTime,
                    generated: true,
                    generating: false
                } : m
            ));

            message.success(`Generated questions and estimated time (${aiData.estimatedTimeMinutes} mins) for ${module.name}`);
        } catch (error: any) {
            message.error(error.message || "Failed to generate questions");
            setModules(prev => prev.map(m =>
                m.id === module.id ? { ...m, generating: false } : m
            ));
        }
    };

    const openReviewModal = async (moduleId: string) => {
        const mod = modules.find(m => m.id === moduleId);
        if (!mod) return;

        // For existing modules, fetch questions from Moodle if not already loaded
        if (mod.isExisting && !mod.questionsLoaded) {
            setLoadingQuestions(true);
            try {
                const courseId = initialValues?.id;
                const { questions: raw, quizId: fetchedQuizId } = await getMoodleQuizQuestions(courseId, mod.name, mod.moodleInstance || 0);
                const decodeEntities = (s: string) => s
                    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#039;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&');
                const parsed: SubjectiveQuestion[] = raw.map((q: any) => {
                    const codeMatch = q.questiontext?.match(/<pre><code>([\s\S]*?)<\/code><\/pre>/);
                    const codeSnippet = codeMatch ? decodeEntities(codeMatch[1]).trim() : '';
                    const questionText = decodeEntities(
                        (q.questiontext || '')
                            .replace(/<br\s*\/?>\s*<pre><code>[\s\S]*?<\/code><\/pre>/, '')
                            .replace(/<[^>]*>?/gm, '')
                    ).trim() || q.name;
                    const answer = q.graderinfo
                        ? decodeEntities(q.graderinfo.replace(/<[^>]*>?/gm, '')).trim()
                        : '';
                    return {
                        id: Math.random().toString(36).substr(2, 9),
                        moodleId: q.id,
                        question: questionText,
                        answer,
                        codeSnippet
                    };
                });
                setModules(prev => prev.map(m => m.id === moduleId
                    ? { ...m, questions: parsed, questionsLoaded: true, generated: true, moodleInstance: fetchedQuizId || m.moodleInstance }
                    : m
                ));
            } catch {
                message.error('Failed to load questions from Moodle');
                setLoadingQuestions(false);
                return;
            }
            setLoadingQuestions(false);
        }

        setActiveReviewModuleId(moduleId);
        setReviewModalVisible(true);
    };

    const updateQuestionText = (questionId: string, field: 'question' | 'answer' | 'codeSnippet', value: string) => {
        if (!activeReviewModuleId) return;
        setModules(prev => prev.map(m => {
            if (m.id === activeReviewModuleId) {
                return {
                    ...m,
                    questions: m.questions.map(q => q.id === questionId ? { ...q, [field]: value } : q)
                };
            }
            return m;
        }));
    };

    const removeQuestion = (questionId: string) => {
        if (!activeReviewModuleId) return;
        setModules(prev => prev.map(m => {
            if (m.id === activeReviewModuleId) {
                return {
                    ...m,
                    questions: m.questions.filter(q => q.id !== questionId)
                };
            }
            return m;
        }));
    };

    const addManualQuestion = () => {
        if (!activeReviewModuleId) return;
        const newQuestion: SubjectiveQuestion = {
            id: Math.random().toString(36).substr(2, 9),
            question: '',
            answer: '',
            codeSnippet: ''
        };
        setModules(prev => prev.map(m => {
            if (m.id === activeReviewModuleId) {
                return {
                    ...m,
                    questions: [...m.questions, newQuestion]
                };
            }
            return m;
        }));
    };

    const handleFinish = async (values: any) => {
        try {
            const modNamesList = modules.map(m => m.name.trim().toLowerCase());
            const dupSubjMod = modNamesList.find((n, i) => modNamesList.indexOf(n) !== i);
            if (dupSubjMod) {
                message.error(`Module names must be unique. Duplicate: "${modules.find(m => m.name.trim().toLowerCase() === dupSubjMod)?.name}"`);
                return;
            }
            const newModules = modules.filter(m => !m.isExisting);
            const editedModules = modules.filter(m => m.isExisting && m.questionsLoaded);
            const renamedOnlyModules = modules.filter(m => m.isExisting && !m.questionsLoaded && m.originalName && m.name.trim() !== m.originalName.trim());
            setTotalSteps((newModules.length > 0 ? 1 : 0) + (pendingDeletions.length > 0 ? 1 : 0) + (editedModules.length > 0 ? 1 : 0) + (renamedOnlyModules.length > 0 ? 1 : 0) + 1);
            setCurrentStep(0);
            setProcessStatus('active');
            setProgressMessage('Initializing save process...');
            setProgressModalVisible(true);
            setLoading(true);

            const trainingId = values.trainingId;
            const cleanEmails = (str: string) => str ? str.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).join(';') : '';

            const newStartDate = values.isEvergreen ? '' : (values.dateRange?.[0] ? values.dateRange[0].format('YYYY-MM-DD') : '');
            const newEndDate = values.isEvergreen ? '' : (values.dateRange?.[1] ? values.dateRange[1].format('YYYY-MM-DD') : '');

            // Capture field-level changes before save
            const changeDescriptions = detectCommonChanges(initialValues, {
                trainingName: values.trainingName,
                startDate: newStartDate,
                endDate: newEndDate,
                isEvergreen: values.isEvergreen,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingType: values.trainingType,
                trainingLink: values.trainingLink || ''
            });
            const oldModNames = (initialValues?.assessments || '').split('|').map((s: string) => s.replace(/\[.*?\]|#[A-Z]\d+/gi, '').trim()).filter(Boolean);
            const newModNames = modules.map(m => m.name.trim());
            const addedMods = newModNames.filter(m => !oldModNames.includes(m));
            const removedMods = oldModNames.filter((m: string) => !newModNames.includes(m));
            const nameList = (names: string[]) => names.length <= 3 ? names.join(', ') : `${names.length} modules`;
            if (addedMods.length > 0) changeDescriptions.push({ field: 'Modules Added', value: nameList(addedMods) });
            if (removedMods.length > 0) changeDescriptions.push({ field: 'Modules Removed', value: nameList(removedMods) });

            // 1. Process Master Record Update
            setProgressMessage('Syncing program details to Moodle...');
            const finalTrainingData: any = {
                trainingId,
                trainingName: values.trainingName,
                active: isActive,
                startDate: newStartDate,
                endDate: newEndDate,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingLink: values.trainingLink || '',
                audienceEmails: values.trainingType === TrainingType.OPEN ? '' : cleanEmails(values.audienceEmails),
                stakeholderEmails: values.trainingType === TrainingType.OPEN ? '' : cleanEmails(values.stakeholderEmails),
                assessments: modules.map(m => `${m.name.trim()} [${m.description.trim()}] #T${m.estimatedTime || 30} #A${m.attemptLimit || 0}`).join(' | '),
                notificationVersion: values.notificationVersion || '1.0',
                trainingType: values.trainingType,
            };
            await adminUpdateTraining(parseInt(id!), finalTrainingData);
            setCurrentStep(1);

            // 2. Process Deletions
            if (pendingDeletions.length > 0) {
                setProgressMessage(`Removing ${pendingDeletions.length} deleted modules...`);
                const courseId = initialValues?.id;
                await Promise.all(pendingDeletions.map(d => deleteMoodleQuiz(courseId, d.name, d.quizId)));
                setPendingDeletions([]);
                setCurrentStep(prev => prev + 1);
            }

            // 3. Process New Modules — create a Moodle quiz for each, same as the create flow
            if (newModules.length > 0) {
                setProgressMessage(`Creating ${newModules.length} new module(s) in Moodle...`);
                const courseId = initialValues?.id; // Moodle course ID
                const existingCount = modules.filter(m => m.isExisting).length;

                for (let i = 0; i < newModules.length; i++) {
                    const mod = newModules[i];
                    setProgressMessage(`Creating quiz for module: ${mod.name} (${i + 1}/${newModules.length})...`);
                    await createMoodleQuiz(
                        courseId,
                        mod.name,
                        `Assessment: ${mod.description}.`,
                        existingCount + i + 1,
                        mod.questions,
                        mod.estimatedTime || 30,
                        mod.attemptLimit || 0
                    );
                }
                setCurrentStep(prev => prev + 1);
            }

            // 4. Save edited questions for existing modules that were reviewed/modified
            if (editedModules.length > 0) {
                setProgressMessage(`Saving edited questions for ${editedModules.length} module(s)...`);
                const courseId = initialValues?.id;
                for (const mod of editedModules) {
                    const nameChanged = mod.originalName && mod.name.trim() !== mod.originalName.trim();
                    await updateMoodleQuizQuestions(
                        courseId,
                        mod.originalName || mod.name,
                        mod.questions.map(q => ({
                            id: q.moodleId ?? 0,
                            question: q.question,
                            answer: q.answer,
                            codeSnippet: q.codeSnippet || ''
                        })),
                        mod.moodleInstance || 0,
                        -1, -1, -1,
                        nameChanged ? mod.name.trim() : undefined
                    );
                }
                setCurrentStep(prev => prev + 1);
            }

            // 5. Rename-only modules (name changed but questions not opened)
            if (renamedOnlyModules.length > 0) {
                setProgressMessage(`Renaming ${renamedOnlyModules.length} module(s) in Moodle...`);
                const courseId = initialValues?.id;
                for (const mod of renamedOnlyModules) {
                    await updateMoodleQuizQuestions(
                        courseId,
                        mod.originalName!,
                        [],
                        mod.moodleInstance || 0,
                        -1, -1, -1,
                        mod.name.trim()
                    );
                }
                setCurrentStep(prev => prev + 1);
            }

            // All operations done — send notification
            if (notifyParticipant && changeDescriptions.length > 0) {
                const changeSummary = await adminSummarizeChanges(changeDescriptions);
                if (changeSummary) await adminSendProgramUpdateNotification(initialValues?.id, changeSummary, user?.name || user?.username || '');
            }

            setProcessStatus('success');
            setProgressMessage('Program updated successfully!');
            setTimeout(() => { setProgressModalVisible(false); setLoading(false); }, 1500);

        } catch (error: any) {
            setProcessStatus('exception');
            setProgressMessage(`Error: ${error.message || 'Failed to update training program'}`);
            setLoading(false);
        }
    };

    const handleCloseProgram = () => {
        const trainingId = form.getFieldValue('trainingId');
        const trainingName = form.getFieldValue('trainingName');

        Modal.confirm({
            title: 'Are you sure you want to close this program?',
            icon: <StopOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div>
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff1f0', borderRadius: '8px', border: '1px solid #ffa39e' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{trainingName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>ID: {trainingId}</div>
                    </div>
                    <p>Once a program is closed (Inactive), it:</p>
                    <ul style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        <li>Cannot be reactivated again</li>
                        <li>Cannot be edited further</li>
                        <li>Participants will see it as "Closed" on their dashboard</li>
                    </ul>
                    <p>Do you wish to proceed?</p>
                </div>
            ),
            okText: 'Yes, Close Program',
            okType: 'danger',
            cancelText: 'No, Keep Active',
            onOk: async () => {
                try {
                    setLoading(true);
                    await adminToggleTrainingStatus(parseInt(id!), true);
                    await adminClearSubmissionsByTraining(trainingId);
                    message.success('Program closed successfully');
                    await fetchTrainingData(parseInt(id!));
                    setIsActive(false); // Override stale Moodle cache if fetchTrainingData returns stale active=true
                } catch (error) {
                    message.error('Failed to close program');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handlePublish = async () => {
        try {
            setLoading(true);
            await publishMoodleProgram(parseInt(id!));
            setIsPublished(true);
            message.success('Program published! Participants can now access it.');
        } catch { message.error('Failed to publish program'); }
        finally { setLoading(false); }
    };

    const handleDeleteTraining = async () => {
        if (!initialValues) return;
        if (deleteConfirmationText.trim() !== initialValues.trainingName.trim()) {
            message.error('Program name does not match');
            return;
        }
        try {
            setLoading(true);
            await adminDeleteTraining(parseInt(id!), initialValues.trainingId);
            message.success('Program deleted successfully');
            setIsDeleteModalVisible(false);
            navigate('/admin');
        } catch (error) {
            message.error('Failed to delete program');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        if (processStatus === 'exception') {
            setProgressModalVisible(false);
        }
    };

    const columns = [
        {
            title: 'Module Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            render: (_: any, record: ModuleEntry) => (
                <Input
                    placeholder="e.g. React Basics"
                    value={record.name}
                    onChange={(e) => updateModuleField(record.id, 'name', e.target.value)}
                />
            )
        },
        {
            title: 'Topic Focus / Description',
            dataIndex: 'description',
            key: 'description',
            width: '30%',
            render: (_: any, record: ModuleEntry) => (
                record.isExisting ? (
                    <Text type="secondary">{record.description}</Text>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Input
                            placeholder="e.g. Hooks and State Management"
                            value={record.description}
                            onChange={(e) => updateModuleField(record.id, 'description', e.target.value)}
                            disabled={!isActive}
                        />
                        <Input
                            placeholder="Extra question prompt (e.g. coding Qs only) (optional)"
                            value={record.extraPrompt}
                            onChange={(e) => updateModuleField(record.id, 'extraPrompt', e.target.value)}
                            disabled={!isActive}
                            size="small"
                        />
                    </Space>
                )
            )
        },
        {
            title: 'Criteria',
            key: 'criteria',
            width: '20%',
            render: (_: any, record: ModuleEntry) => (
                record.isExisting ? (
                    <Text type="secondary">-</Text>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Select
                            value={record.difficulty}
                            onChange={(val) => updateModuleField(record.id, 'difficulty', val)}
                            style={{ width: '100%' }}
                            disabled={!isActive}
                        >
                            <Option value="Easy">Easy</Option>
                            <Option value="Medium">Medium</Option>
                            <Option value="Hard">Hard</Option>
                        </Select>
                        <InputNumber
                            min={1}
                            max={20}
                            value={record.questionCount}
                            onChange={(val) => updateModuleField(record.id, 'questionCount', val)}
                            style={{ width: '100%' }}
                            addonBefore="Qty"
                            disabled={!isActive}
                        />
                    </Space>
                )
            )
        },
        {
            title: 'AI Generator',
            key: 'generator',
            width: '25%',
            render: (_: any, record: ModuleEntry) => (
                record.isExisting ? (
                    <Space direction="vertical">
                        <Tag color="success" icon={<CheckCircleFilled />}>Ready & Saved</Tag>
                        <Button
                            type="link"
                            size="small"
                            loading={loadingQuestions && activeReviewModuleId === record.id}
                            disabled={loadingQuestions && activeReviewModuleId !== record.id}
                            onClick={() => openReviewModal(record.id)}
                        >
                            Review / Edit
                        </Button>
                    </Space>
                ) : (
                    <Space direction="vertical">
                        <Button
                            type={record.generated ? 'default' : 'primary'}
                            icon={<RobotOutlined />}
                            loading={record.generating}
                            onClick={() => handleGenerateForModule(record)}
                            disabled={!isActive || (modules.some(m => m.generating) && !record.generating)}
                        >
                            {record.generating
                                ? 'Generating'
                                : record.generated ? 'Regenerate' : 'Generate'}
                        </Button>
                        {record.generated && (
                            <Space>
                                <Tag color="success" icon={<CheckCircleFilled />}>{record.questions.length} Ready</Tag>
                                <Button type="link" size="small" onClick={() => openReviewModal(record.id)}>Review / Edit</Button>
                            </Space>
                        )}
                    </Space>
                )
            )
        },
        {
            title: 'Time (Mins)',
            dataIndex: 'estimatedTime',
            width: 100,
            render: (val: number, record: ModuleEntry) => (
                <InputNumber
                    min={1}
                    max={300}
                    value={val}
                    onChange={(v) => updateModuleField(record.id, 'estimatedTime', v || 30)}
                    disabled={!isActive}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Max Attempts',
            dataIndex: 'attemptLimit',
            width: 110,
            render: (val: number, record: ModuleEntry) => (
                <InputNumber
                    min={0}
                    max={99}
                    value={val}
                    onChange={(v) => updateModuleField(record.id, 'attemptLimit', v ?? 0)}
                    placeholder="0=∞"
                    disabled={!isActive}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: '',
            key: 'action',
            width: 50,
            render: (_: any, record: ModuleEntry) => (
                <Popconfirm
                    title="Delete this module?"
                    description="This will remove the module and all its questions permanently on save."
                    onConfirm={() => removeModuleRow(record)}
                    okText="Yes"
                    cancelText="No"
                    disabled={modules.length <= 1 || !isActive}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} disabled={modules.length <= 1 || !isActive} />
                </Popconfirm>
            )
        }
    ];

    const activeReviewModule = modules.find(m => m.id === activeReviewModuleId);
    const percent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);

    if (fetching) {
        return (
            <div style={{ padding: '100px', textAlign: 'center' }}>
                <Spin size="large" tip="Loading training details..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: '16px' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')}>
                    Back to Admin
                </Button>
                <Button
                    icon={<LineChartOutlined />}
                    type="primary"
                    ghost
                    style={{ borderColor: '#fa541c', color: '#fa541c' }}
                    onClick={() => navigate(`/training/${encodeURIComponent(form.getFieldValue('trainingId'))}?mode=analyze`)}
                >
                    Analyze
                </Button>
            </div>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{ notificationVersion: '1.0', trainingType: TrainingType.OPEN, ...form.getFieldsValue() }}
            >
                <Card title="1. Program Details" style={{ marginBottom: '24px', borderRadius: '12px' }}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item name="trainingId" label="Training ID">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="passingPercentage" label="Passing %" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} disabled={!isActive}
                                    formatter={v => v ? `${v}%` : ''} parser={v => v ? parseFloat(v.replace('%', '')) : 0 as any} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="estimatedEffortHours" label="Assessment Duration (Hours)">
                                <InputNumber min={0} style={{ width: '100%' }} disabled={!isActive} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="trainingName" label="Training Program Name" rules={[{ required: true }]}>
                        <Input size="large" disabled={!isActive} maxLength={250} showCount />
                    </Form.Item>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.trainingType !== curr.trainingType || prev.isEvergreen !== curr.isEvergreen}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const trainingType = getFieldValue('trainingType');
                                    const isEvergreen = getFieldValue('isEvergreen');
                                    if (trainingType !== TrainingType.OPEN && isEvergreen) {
                                        setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    }
                                    return (
                                        <>
                                            {trainingType === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" disabled={!isActive} />
                                                </Form.Item>
                                            )}
                                            {!isEvergreen || trainingType !== TrainingType.OPEN ? (
                                                <Form.Item label="Training Period" name="dateRange" rules={[{ required: true, message: 'Please select a training period' }]}
                                                    extra="Users can only start training on or after the start date.">
                                                    <DatePicker.RangePicker style={{ width: '100%' }} size="large" disabled={!isActive} />
                                                </Form.Item>
                                            ) : (
                                                <Alert message="Evergreen Program" description="This training has no fixed dates and will be available immediately."
                                                    type="info" showIcon style={{ marginBottom: 24 }} />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Resource / Documentation Link" name="trainingLink">
                                <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="https://..." style={{ borderRadius: '8px' }} disabled={!isActive} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Form.Item label="Training Type" name="trainingType" rules={[{ required: true, message: 'Please select a training type' }]}>
                        <Radio.Group disabled={!isActive}>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}>
                                    <Text strong>Open for All</Text>
                                    <br />
                                    <Text type="secondary">No nominations required. Anyone can join and take the assessment anytime.</Text>
                                </Radio>
                                <Radio value={TrainingType.REQUESTABLE}>
                                    <Text strong>Nominated + Request to Join</Text>
                                    <br />
                                    <Text type="secondary">Specific people are nominated. Others can request to join before the start date.</Text>
                                </Radio>
                                <Radio value={TrainingType.PRIVATE}>
                                    <Text strong>Private (Invite Only)</Text>
                                    <br />
                                    <Text type="secondary">Only people explicitly nominated can see and access this program.</Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />
                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType}>
                        {({ getFieldValue }) => {
                            const type = getFieldValue('trainingType');
                            if (type === TrainingType.OPEN) return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Participants (Emails, semicolon separated)" name="audienceEmails" rules={[{ required: true }]}>
                                            <TextArea rows={4} placeholder="user1@company.com; user2@company.com" style={{ borderRadius: '8px' }} disabled={!isActive} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Stakeholders (Emails, semicolon separated)" name="stakeholderEmails" rules={[{ required: true }]}>
                                            <TextArea rows={4} placeholder="manager@company.com; director@company.com" style={{ borderRadius: '8px' }} disabled={!isActive} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>
                </Card>

                <Card
                    title="2. Module Setup & Question Generation"
                    style={{ marginBottom: '24px', borderRadius: '12px' }}
                    extra={
                        isActive && (
                            <Button type="dashed" onClick={addModuleRow} icon={<PlusOutlined />} style={{ borderColor: '#1890ff', color: '#1890ff' }}>
                                Add Module
                            </Button>
                        )
                    }
                >
                    <Alert
                        message="AI Generation Workflow"
                        description="Add new modules, describe the topic, and select difficulty. Existing modules can be removed, but their questions cannot be re-generated directly here."
                        type="info"
                        showIcon
                        style={{ marginBottom: '24px', borderRadius: '8px' }}
                    />

                    <Table
                        dataSource={modules}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                        locale={{ emptyText: <div style={{ padding: '20px', color: '#999' }}>Click "Add Module" to start adding curriculum.</div> }}
                        bordered
                    />
                </Card>

                <div style={{ textAlign: 'right', marginTop: '32px', marginBottom: '50px' }}>
                    <Space size="large">
                        {(!isActive || !isPublished) && (
                            <Button danger size="large" icon={<DeleteOutlined />}
                                onClick={() => { setDeleteConfirmationText(''); setIsDeleteModalVisible(true); }}>
                                Delete Program
                            </Button>
                        )}
                        <Button onClick={() => navigate('/admin')} size="large" disabled={loading}>Cancel</Button>
                        {isActive && isPublished && (
                            <Popconfirm title="Close Program"
                                description="Are you sure you want to close this program? This action cannot be undone."
                                onConfirm={handleCloseProgram} okText="Yes, Close" cancelText="No">
                                <Button danger size="large" icon={<StopOutlined />}>Close Program</Button>
                            </Popconfirm>
                        )}
                        {isActive && (
                            <Tooltip title={notifyParticipant ? 'Participants will be notified by email on save' : 'Participants will NOT be notified on save'}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: notifyParticipant ? '#f0f5ff' : '#fafafa', borderRadius: 6, border: `1px solid ${notifyParticipant ? '#adc6ff' : '#d9d9d9'}`, cursor: 'default' }}>
                                    <BellOutlined style={{ color: notifyParticipant ? '#1890ff' : '#bfbfbf', fontSize: 14 }} />
                                    <Typography.Text style={{ fontSize: 13, color: notifyParticipant ? '#1890ff' : '#8c8c8c', whiteSpace: 'nowrap' }}>Notify Participants</Typography.Text>
                                    <Switch size="small" checked={notifyParticipant} onChange={setNotifyParticipant} />
                                </div>
                            </Tooltip>
                        )}
                        {isActive && !isPublished && (
                            <Button size="large" icon={<SaveOutlined />} loading={loading}
                                onClick={() => { form.submit(); }}>
                                Save as Draft
                            </Button>
                        )}
                        {isActive && !isPublished && (
                            <Button type="primary" size="large" icon={<SendOutlined />} loading={loading} onClick={handlePublish}>
                                Publish Program
                            </Button>
                        )}
                        {isActive && isPublished && (
                            <Button type="primary" size="large" icon={<SaveOutlined />} htmlType="submit" loading={loading}>
                                Save Changes
                            </Button>
                        )}
                    </Space>
                </div>
            </Form>

            {/* Review Modal */}
            <Modal
                title={`Review Questions: ${activeReviewModule?.name}`}
                open={reviewModalVisible}
                onCancel={() => setReviewModalVisible(false)}
                footer={[
                    <Button key="add" icon={<PlusOutlined />} onClick={addManualQuestion} style={{ float: 'left' }}>Add Question</Button>,
                    <Button key="done" type="primary" onClick={() => setReviewModalVisible(false)}>Done Reviewing</Button>
                ]}
                width={1000}
                centered
            >
                {activeReviewModule?.questions.map((q, index) => (
                    <Card
                        key={q.id}
                        style={{ marginBottom: 16, backgroundColor: '#f9f9f9', border: '1px solid #e8e8e8' }}
                        size="small"
                        extra={
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeQuestion(q.id)}
                            >
                                Remove
                            </Button>
                        }
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>Q{index + 1}: Question Detail</Text>
                            <TextArea
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                value={q.question}
                                onChange={(e) => updateQuestionText(q.id, 'question', e.target.value)}
                            />

                            <Text strong>Expected Answer / Criteria</Text>
                            <TextArea
                                autoSize={{ minRows: 3, maxRows: 8 }}
                                value={q.answer}
                                onChange={(e) => updateQuestionText(q.id, 'answer', e.target.value)}
                            />

                            <Text strong>Code Snippet (Optional)</Text>
                            <TextArea
                                style={{ fontFamily: 'monospace' }}
                                autoSize={{ minRows: 1, maxRows: 6 }}
                                value={q.codeSnippet}
                                placeholder="Any reference code goes here..."
                                onChange={(e) => updateQuestionText(q.id, 'codeSnippet', e.target.value)}
                            />
                        </Space>
                    </Card>
                ))}
            </Modal>

            {/* Delete Modal */}
            <Modal
                title={<span style={{ color: 'red' }}>Delete Program: {initialValues?.trainingName} ({initialValues?.trainingId})</span>}
                open={isDeleteModalVisible}
                onCancel={() => {
                    setIsDeleteModalVisible(false);
                    setDeleteConfirmationText('');
                }}
                onOk={handleDeleteTraining}
                okText="Permanently Delete"
                okButtonProps={{ 
                    danger: true, 
                    disabled: deleteConfirmationText.trim() !== initialValues?.trainingName?.trim(), 
                    loading: loading 
                }}
                centered
            >
                <Alert
                    message="Warning: This action cannot be undone."
                    description="This will permanently delete the training program and ALL associated data from Moodle."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Text style={{ display: 'block', marginBottom: 8 }}>
                    Please type <strong>{initialValues?.trainingName}</strong> to confirm:
                </Text>
                <Input
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder="Type program name exactly"
                    maxLength={250}
                />
            </Modal>

            {/* Progress / Blocking Modal */}
            <Modal
                title={
                    <Space>
                        {processStatus === 'active' && <LoadingOutlined />}
                        <span>{processStatus === 'active' ? 'Saving Program...' : 'Update Complete'}</span>
                    </Space>
                }
                open={progressModalVisible}
                closable={processStatus === 'exception'}
                maskClosable={processStatus === 'exception'}
                onCancel={handleCloseModal}
                footer={processStatus === 'exception' ? [
                    <Button key="close" onClick={handleCloseModal}>Close</Button>
                ] : null}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Progress
                        type="circle"
                        percent={percent}
                        status={processStatus}
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': '#87d068',
                        }}
                    />
                    <div style={{ marginTop: '24px' }}>
                        <Text strong style={{ fontSize: '16px' }}>
                            {processStatus === 'exception' ? 'An error occurred' : progressMessage}
                        </Text>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EditSubjectiveProgram;
